/** Service Desk console: Service settings, part A - the menu, General (prefix, words), Ticket types and the type editor, Fields, Statuses (SD-S1-47). */
export const serviceSettingsA = {
  nav: {
    label: "Service settings sections",
    locked: "Not on your plan",
    general: "General & numbering",
    types: "Ticket types",
    fields: "Fields",
    statuses: "Statuses",
  },
  soon: {
    title: "This section opens with a later release",
    body: "The address is right, the screen is not built yet. Your settings are safe and nothing is missing on your side.",
    back: "Back to General",
  },
  locked: {
    title: "{{section}} is included from the {{plan}} plan",
    body: "Your existing settings and tickets stay as they are. Upgrade to change this section.",
  },
  loading: "Loading",
  labelPair: {
    en: "{{legend}} (English)",
    fil: "{{legend}} (Filipino)",
    filHint: "Optional. Falls back to English.",
  },
  sort: {
    up: "Move {{name}} up",
    down: "Move {{name}} down",
    moved: "Moved {{name}} to position {{position}} of {{total}}",
  },
  groups: {
    problems: "Problems",
    requests: "Requests",
    orders: "Orders & billing",
    feedback: "Feedback",
  },
  numbering: {
    title: "Ticket numbers",
    intro: "Every ticket gets a number made of this prefix and a counter, like the example below. You can set the prefix once, before the first ticket exists.",
    lockedIntro: "The prefix is locked: it can be set once, before the first ticket exists, and that has happened.",
    lockedBadge: "Locked",
    label: "Ticket number prefix",
    hint: "2 to 5 capital letters. Tickets are numbered like {{example}}.",
    invalid: "Use 2 to 5 letters, like KHA.",
    save: "Save prefix",
    saved: "Ticket prefix set to {{prefix}}.",
    confirmTitle: "Use {{prefix}} for every ticket number?",
    confirmBody: "The prefix changes from {{from}} to {{to}} and cannot be changed again. Tickets already created keep the number they have.",
    confirm: "Set prefix",
  },
  vocabulary: {
    title: "Words your customers see",
    intro: "Rename the terms of your industry in the console, the portal and e-mails. Leave a word empty to keep the standard one. A template pack may already have set some.",
    en: "English",
    fil: "Filipino",
    save: "Save words",
    discard: "Discard changes",
    saved: "Words saved.",
    terms: {
      organisation: "Organisation",
      contact: "Contact",
      asset: "Asset",
      project: "Project",
      project_plural: "Project (plural)",
      stage: "Stage",
      stage_plural: "Stage (plural)",
      open_item: "Open item",
      visit: "Visit",
    },
    defaults: {
      organisation: "Organisation",
      contact: "Contact",
      asset: "Asset",
      project: "Project",
      project_plural: "Installations",
      stage: "Stage",
      stage_plural: "Stages",
      open_item: "Open item",
      visit: "Visit",
    },
  },
  errors: {
    planLimit: {
      types: "Your plan's limit of custom ticket types is reached. Archive one you no longer use, or upgrade.",
      fields: "Your plan's limit of custom fields is reached. Remove one you no longer use, or upgrade.",
      statuses: "Your plan's limit of custom statuses is reached. Delete one you no longer use, or upgrade.",
    },
    typeInUse_one: "{{count}} ticket uses this type. Archive it instead of deleting it.",
    typeInUse_other: "{{count}} tickets use this type. Archive it instead of deleting it.",
    statusInUse_one: "{{count}} ticket is in this status. Choose a replacement status of the same category.",
    statusInUse_other: "{{count}} tickets are in this status. Choose a replacement status of the same category.",
    codeInUse: "Tickets end with this code. Deactivate it instead of deleting it.",
    systemRecord: "This one is built in: relabel or switch it off instead of deleting it.",
    prefixLocked: "The ticket number prefix can be changed once, before the first ticket exists. It is locked now.",
    fieldInUse: "A ticket type or a live workflow still uses this field. Remove it there first.",
    fieldHasAnswers_one: "{{count}} record holds an answer for this field. Remove it from the ticket types instead of deleting it, so the answers stay readable.",
    fieldHasAnswers_other: "{{count}} records hold an answer for this field. Remove it from the ticket types instead of deleting it, so the answers stay readable.",
    fieldDependents: "Other fields of this type depend on this one. Change those fields first.",
  },
  flags: {
    ai: "AI can fill",
    pii: "Personal data",
    hiddenAi: "Never sent to AI",
    clientRequired: "Client required",
    staffCreate: "Staff: on create",
    staffResolve: "Staff: before resolve",
    client: {
      hidden: "Hidden from clients",
      edits: "Clients edit",
      sees: "Clients see",
    },
  },
  types: {
    title: "Ticket types",
    new: "New custom type",
    builtIn: "Built-in",
    custom: "Custom",
    archived: "Archived",
    planLocked: "Not on your plan",
    category: { customer: "Customer", back_office: "Back office", tracker: "Tracker" },
    categoryHint: {
      customer: "Clients can see and pick it.",
      back_office: "Internal: clients never see it.",
      tracker: "Links many customer tickets to one cause.",
    },
    asset: { no: "Equipment not asked", optional: "Equipment optional", required: "Equipment required" },
    group: "Portal group",
    equipment: "Equipment",
    priority: "Default priority",
    team: "Default team",
    noTeam: "No default team",
    counts: "Tickets: {{tickets}} · Fields: {{fields}}",
    pick: "Clients can pick",
    pickAria: "Clients can pick {{name}}",
    pickOn: "Clients can now pick {{name}}.",
    pickOff: "{{name}} is hidden from clients.",
    notShown: "Not shown to clients",
    edit: "Edit",
    editAria: "Edit {{name}}",
    archive: "Archive",
    archiveAria: "Archive {{name}}",
    restore: "Restore",
    restoreAria: "Restore {{name}}",
    archiveTitle: "Archive this type?",
    archiveBody_one: "{{count}} ticket uses {{name}}. Archiving keeps it as it is; clients and the assistant just stop offering the type.",
    archiveBody_other: "{{count}} tickets use {{name}}. Archiving keeps them as they are; clients and the assistant just stop offering the type.",
    archiveConfirm: "Archive type",
    archivedToast: "{{name}} archived.",
    restoredToast: "{{name}} restored.",
    emptyTitle: "No ticket types yet",
    emptyBody: "Ticket types decide the form a request is raised with. Apply a template pack in General, or create your own.",
    footPlain: "Custom types: {{count}}.",
    footLimited: "Custom types: {{count}} of {{limit}} on {{plan}}.",
    footUnlimited: "Custom types: {{count}}. No limit on {{plan}}.",
    footNote: "Archived types keep their existing tickets.",
  },
  typeNew: {
    title: "New custom type",
    description: "Name it and say who sees it. You add its fields, conditions and intake guidance in the editor next.",
    name: "Name",
    category: "Category",
    group: "Portal group",
    groupHint: "Where clients find it in the portal and the widget.",
    create: "Create type",
    created: "{{name}} created. Add its fields next.",
    errors: {
      nameRequired: "Give the type a name.",
      nameShort: "Use at least {{min}} characters.",
      nameLong: "Use {{max}} characters or fewer.",
      nameTaken: "A type with this name already exists.",
    },
  },
  editor: {
    back: "Service settings",
    subtitleBuiltIn_one: "Built-in type · {{audience}} · {{count}} ticket uses it.",
    subtitleBuiltIn_other: "Built-in type · {{audience}} · {{count}} tickets use it.",
    subtitleCustom_one: "Custom type · {{audience}} · {{count}} ticket uses it.",
    subtitleCustom_other: "Custom type · {{audience}} · {{count}} tickets use it.",
    audienceClients: "visible to clients",
    audienceInternal: "internal",
    archivedBanner: "This type is archived. Existing tickets keep it; clients and the assistant no longer offer it.",
    duplicate: "Duplicate",
    duplicated: "Copy created: {{name}}.",
    duplicatePartial_one: "Copy created, but {{count}} field could not be copied. Add it by hand.",
    duplicatePartial_other: "Copy created, but {{count}} fields could not be copied. Add them by hand.",
    delete: "Delete",
    deleteTitle: "Delete {{name}}?",
    deleteBody: "This removes the type and its field list. A type that tickets use cannot be deleted: archive it instead.",
    deleteConfirm: "Delete type",
    deleted: "{{name}} deleted.",
    details: "Name and behaviour",
    name: "Name",
    description: "Description",
    icon: "Icon",
    iconHint: "Shown beside the name in the widget and the portal.",
    iconDefault: "Default icon",
    group: "Portal group",
    groupHint: "Where clients find it.",
    groupNone: "No group",
    category: "Category",
    categoryHint: "The category never changes after creation.",
    priority: "Default priority",
    team: "Default team",
    teamNone: "No default team",
    teamUnavailable: "You cannot read teams, so the team stays as it is.",
    sla: "SLA policy",
    slaInherit: "Follow the contract or workspace default",
    slaHint: "Pin one of your ticket SLA policies to this type.",
    slaUnavailable: "You cannot read SLA policies, so this stays as it is.",
    equipment: "Equipment",
    clientSelectable: "Clients can pick this type",
    clientSelectableHint: "The assistant can choose it too. Back-office types are never offered.",
    planLockedHint: "Your plan does not include what this type needs, so clients cannot pick it yet.",
    verified: "Clients must be verified first",
    verifiedHint: "An unverified visitor cannot raise this type.",
    resolutionCode: "Staff pick a resolution code",
    resolutionCodeHint: "Required when a ticket of this type is resolved.",
    site: "Ask for the site",
    siteHint: "Tickets of this type are about a site or an installation.",
    order: "Ask for the order",
    orderHint: "Tickets of this type are about an order.",
    visit: "May need a visit",
    visitHint: "Staff can schedule a field visit from these tickets.",
    yes: "Yes",
    no: "No",
    fields: "Fields",
    fieldCount: "{{count}} of {{max}}",
    addField: "Add field",
    addFirstField: "Add the first field",
    fieldsEmptyTitle: "No custom fields yet",
    fieldsEmptyBody: "This type only asks for a title and a description. Add fields so staff get the details they need and the assistant can fill them from the chat.",
    fieldsOrderLabel: "Fields in the order clients see them",
    fieldsFull: "A type can carry up to {{max}} fields.",
    systemFields: "Every ticket also has the system fields: number, type, title, description, status, priority, requester, organisation, site, assets, team, assignee, tags and channel.",
    editField: "Edit",
    editFieldAria: "Edit {{name}}",
    removeFieldAria: "Remove {{name}} from this type",
    removeTitle: "Remove {{name}} from this type?",
    removeBody: "Values already on tickets are kept, only hidden. The field stays available to other types.",
    removeConfirm: "Remove field",
    fieldRemoved: "{{name}} removed from this type.",
    onlyIf: "Only if {{field}} is {{values}}",
    guidance: "Intake guidance for the assistant",
    guidanceLabel: "What should the assistant ask?",
    guidanceHint: "Written by you and wrapped as system guidance; clients cannot change it. {{count}} of {{max}} characters.",
    guidanceSafety: "The assistant only asks for what is listed here and in the fields. It never promises warranty cover and never asks for ID numbers or health data.",
    unsaved: "Unsaved changes",
    unsavedText: "You have unsaved changes",
    discard: "Discard",
    save: "Save changes",
    saved: "Ticket type saved.",
    notFoundTitle: "This ticket type does not exist",
    notFoundBody: "It may have been deleted. Pick a type from Service settings.",
    errors: {
      nameRequired: "Give the type a name.",
      nameShort: "Use at least {{min}} characters.",
      nameLong: "Use {{max}} characters or fewer.",
      nameFil: "The Filipino name needs {{min}} to {{max}} characters, or leave it empty.",
      descriptionLong: "Use {{max}} characters or fewer.",
      guidanceLong: "Use {{max}} characters or fewer.",
    },
  },
  fieldDrawer: {
    addTitle: "Add a field",
    editTitle: "Edit field: {{name}}",
    hint: "The field itself is shared with every type that uses it; the settings below apply to this type only.",
    mode: "Where the field comes from",
    modeExisting: "Existing field",
    modeNew: "New field",
    existing: "Field",
    existingHint: "Fields your workspace already defined and this type does not have yet.",
    noneAvailable: "Every field you defined is already on this type. Create a new one.",
    forType: "For this type",
    visible: "Clients see this field",
    visibleHint: "Hidden fields are for staff only.",
    editable: "Clients can edit it",
    editableHint: "Appears in the form clients fill, not just on their ticket.",
    required: "Clients must fill it",
    requiredHint: "The ticket cannot be sent without it.",
    staff: "Staff must fill it",
    staffHint: "Staff are blocked until it has a value.",
    staffOptions: { none: "Not required", create: "When the ticket is created", resolve: "Before the ticket is resolved" },
    condition: "Show only when…",
    conditionHint: "Parents can be dropdown or yes/no fields of this type.",
    conditionNone: "Add a dropdown or yes/no field to this type first.",
    conditionAlways: "Always show",
    conditionValues: "…is one of",
    add: "Add field",
    save: "Save field",
    added: "{{name}} added to this type.",
    updated: "{{name}} updated.",
    errors: {
      clientFlags: "A field must be visible to clients before they can edit or be required to fill it.",
      conditionValues: "Tick at least one value that shows this field.",
      conditionMany: "Use 20 values or fewer.",
    },
  },
  fieldTypes: {
    text: { name: "Short text", hint: "One line, up to 500 characters." },
    long_text: { name: "Long text", hint: "Several lines, up to 5,000 characters." },
    number: { name: "Number", hint: "Any number, with an optional range." },
    integer: { name: "Whole number", hint: "A count; no decimals." },
    bool: { name: "Yes / no", hint: "A single choice between two answers." },
    date: { name: "Date", hint: "A calendar date." },
    datetime: { name: "Date and time", hint: "A date with a time of day." },
    dropdown: { name: "Dropdown", hint: "One choice from your list." },
    multi_select: { name: "Multi-select", hint: "Several choices from your list." },
    asset: { name: "Equipment", hint: "Picks one of the client's equipment records." },
    site: { name: "Site", hint: "Picks one of the client's sites." },
    order: { name: "Order", hint: "Picks one of the client's orders." },
    contact: { name: "Contact", hint: "Picks a colleague at the client's organisation." },
    files: { name: "Files", hint: "Photos, videos or documents." },
    serial: { name: "Serial number", hint: "Text with a format hint shown under the box." },
  },
  fieldForm: {
    label: "Label",
    key: "Key",
    keyHint: "snake_case, up to {{max}} characters. It never changes after the field is created.",
    keyLocked_one: "The key and the type never change. {{count}} ticket type uses this field.",
    keyLocked_other: "The key and the type never change. {{count}} ticket types use this field.",
    type: "Type",
    options: "Options",
    optionsHint: "One per line: English, or English | Filipino. Renaming an option keeps the answers already stored.",
    help: "Help text (English)",
    helpFil: "Help text (Filipino)",
    bounds: {
      min: "Smallest value",
      max: "Largest value",
      maxLength: "Longest answer (characters)",
      maxFiles: "Most files",
      maxSelected: "Most choices",
      formatHint: "Format hint",
    },
    ai: "The assistant may fill it from the chat",
    aiHint: "Lets the intake assistant propose a value from what the client wrote.",
    pii: "Contains personal data",
    piiHint: "Redacted in logs and exports.",
    hiddenAi: "Never send the value to the AI",
    hiddenAiHint: "For internal figures such as costs. It also stops the assistant from filling it.",
    errors: {
      keyRequired: "Enter a key.",
      keyShape: "Use snake_case: lowercase letters, numbers and underscores, starting with a letter, up to {{max}} characters.",
      keyTaken: "A field with this key already exists.",
      labelRequired: "Give the field a label.",
      long: "Use {{max}} characters or fewer.",
      number: "Enter a number.",
      whole: "Enter a whole number.",
      positive: "Enter a number from 1 up.",
      range: "The largest value must not be below the smallest.",
      between: "Enter a whole number from {{min}} to {{max}}.",
      optionsFew: "Add at least two options.",
      optionsMany: "Use {{max}} options or fewer.",
      optionsRepeat: "Each option must be different.",
      optionsValue: "An option could not be turned into a stored value. Use letters or numbers in its name.",
    },
  },
  fields: {
    note: "Fields belong to ticket types. Build and reorder them in the type editor; here you define them once for every type.",
    openTypes: "Open ticket types",
    title: "Fields",
    objectLabel: "Fields of",
    objects: { ticket: "Tickets", organisation: "Organisations", contact: "Contacts" },
    new: "New field",
    newTitle: "New field",
    editTitle: "Edit field",
    dialogHint: "Define what the field is. How a type uses it (order, who sees it, conditions) is set in the type editor.",
    create: "Create field",
    save: "Save field",
    created: "{{name}} created.",
    updated: "{{name}} updated.",
    edit: "Edit",
    editAria: "Edit {{name}}",
    deleteAria: "Delete {{name}}",
    deleteTitle: "Delete {{name}}?",
    deleteBody: "A field that a ticket type, a live workflow or a stored answer still uses cannot be deleted: remove it from the ticket types instead, so the answers stay readable.",
    deleteConfirm: "Delete field",
    deleted: "{{name}} deleted.",
    emptyTitle: "No fields here yet",
    emptyBody: {
      ticket: "Fields collect the details a ticket needs. Create one, then add it to a ticket type.",
      organisation: "Organisation fields hold what you track about each client company, like an account tier or a contract number.",
      contact: "Contact fields hold what you track about each person, like a department or a preferred branch.",
    },
    moreOptions: "+{{count}} more",
    usedBy_one: "Used by {{count}} ticket type.",
    usedBy_other: "Used by {{count}} ticket types.",
    usedByRecords: "Shown on the record itself.",
    footPlain: "Fields defined: {{count}}.",
    footLimited: "Fields defined: {{count}} of {{limit}} on {{plan}}.",
    footUnlimited: "Fields defined: {{count}}. No limit on {{plan}}.",
    footNote: "Ticket, organisation and contact fields count together.",
  },
  codes: {
    title: "Resolution codes",
    intro: "Staff pick one when they resolve or cancel a ticket. A code that skips CSAT never sends a satisfaction survey.",
    new: "New code",
    newTitle: "New resolution code",
    editTitle: "Edit resolution code",
    label: "Label",
    key: "Key",
    keyHint: "Made from the English label; lowercase letters, numbers and underscores. It never changes.",
    keyLocked: "The key never changes.",
    usable: "Staff can use it when a ticket is",
    category: { resolved: "Resolved", closed: "Closed or cancelled" },
    skipsCsat: "Skip the CSAT survey",
    skipsCsatHint: "For outcomes the client did not experience, like duplicates and spam.",
    create: "Create code",
    save: "Save code",
    created: "{{name}} created.",
    updated: "{{name}} updated.",
    builtIn: "Built-in",
    noCsat: "No CSAT survey",
    uses_one: "{{count}} ticket ends with it",
    uses_other: "{{count}} tickets end with it",
    activeAria: "{{name}} is in use",
    edit: "Edit",
    editAria: "Edit {{name}}",
    deleteAria: "Delete {{name}}",
    deleteTitle: "Delete {{name}}?",
    deleteBody: "A code that tickets end with cannot be deleted: switch it off instead.",
    deleteConfirm: "Delete code",
    deleted: "{{name}} deleted.",
    emptyTitle: "No resolution codes",
    emptyBody: "Staff need at least one code to resolve a ticket. Create the first one.",
    errorTitle: "Resolution codes couldn't load",
    errors: {
      labelRequired: "Give the code a label.",
      keyShape: "Use lowercase letters, numbers and underscores, starting with a letter.",
      keyTaken: "A code with this key already exists.",
      categories: "Pick at least one situation.",
    },
  },
  statuses: {
    title: "Statuses",
    new: "New status",
    builtIn: "Built-in",
    custom: "Custom",
    default: "Default for its category",
    pauses: "Pauses the resolution clock",
    notSelectable: "Over your plan: kept for existing tickets",
    clientEn: "Client (English):",
    clientFil: "Client (Filipino):",
    uses_one: "{{count}} ticket is in it",
    uses_other: "{{count}} tickets are in it",
    notify: "Tell the client",
    notifyAria: "Tell the client when a ticket enters {{name}}",
    edit: "Edit",
    editAria: "Edit {{name}}",
    deleteAria: "Delete {{name}}",
    deleteTitle: "Delete {{name}}?",
    deleteUnused: "No ticket is in this status, so it is deleted straight away.",
    deleteUsed_one: "{{count}} ticket is in this status. It moves to the status you choose below.",
    deleteUsed_other: "{{count}} tickets are in this status. They move to the status you choose below.",
    replacement: "Move its tickets to",
    replacementHint: "Only statuses of the same category are offered.",
    replacementChoose: "Choose a status",
    replacementRequired: "Choose a status to move its tickets to.",
    deleteConfirm: "Delete status",
    deleted: "{{name}} deleted.",
    created: "{{name}} created.",
    updated: "{{name}} updated.",
    emptyTitle: "No statuses",
    emptyBody: "Every workspace has the built-in statuses. Reload the page; if this stays empty, contact support.",
    footPlain: "Custom statuses: {{count}}.",
    footLimited: "Custom statuses: {{count}} of {{limit}} on {{plan}}.",
    footUnlimited: "Custom statuses: {{count}}. No limit on {{plan}}.",
    footNote: "Waiting for client always shows to clients as Action needed.",
    categoriesTitle: "How categories behave",
    behaviour: {
      new: "unassigned, waiting to be picked up.",
      open: "being worked on; SLA clocks run.",
      pending: "waiting for the client; SLA clocks pause and reminders start.",
      on_hold: "waiting for a third party; resolution can pause.",
      resolved: "fixed, the client confirms; closes by itself after a few days.",
      closed: "finished; read-only.",
    },
  },
  statusForm: {
    newTitle: "New status",
    editTitle: "Edit status",
    hint: "The six categories are fixed and carry the behaviour. You choose the words, the colour and who gets told.",
    staff: "Staff label",
    client: "Client label",
    category: "Category",
    categoryLocked: "The category of a built-in status, or one in use, cannot change.",
    colour: "Colour",
    colourHint: "Only the shade changes; the category's icon stays.",
    colourDefault: "Category default",
    colours: { neutral: "Neutral", info: "Blue", primary: "Brand", accent: "Amber", success: "Green", warning: "Orange", danger: "Red" },
    preview: "Preview",
    previewSample: "Status",
    notify: "Tell the client when a ticket enters this status",
    notifyHint: "Sends the client the e-mail for this status.",
    pause: "Pause the resolution clock",
    pauseHint: "For statuses that wait on a third party.",
    makeDefault: "Make it the default of its category",
    makeDefaultHint: "Tickets moved to this category land here.",
    create: "Create status",
    save: "Save status",
    errors: {
      staffRequired: "Enter the staff label.",
      clientRequired: "Enter what the client sees.",
      long: "Use {{max}} characters or fewer.",
    },
  },
  preview: {
    title: "Live preview",
    kind: "Preview type",
    widget: "Widget form",
    ai: "AI summary",
    staff: "Staff view",
    widgetLabel: "Widget form preview",
    aiLabel: "AI summary card preview",
    staffLabel: "Staff view preview",
    report: "Report a problem: {{type}}",
    choose: "Choose…",
    addFile: "Add photo or video",
    submit: "Submit",
    edit: "Edit",
    onlyPreview: "Preview only: nothing is sent.",
    noFields: "No fields are shown to clients.",
    widgetHint: "Choose a value in a dropdown or yes/no field to see conditional fields appear.",
    readOnly_one: "{{count}} more field shows on the client's ticket page once staff fill it.",
    readOnly_other: "{{count}} more fields show on the client's ticket page once staff fill them.",
    aiTitle: "Here is what I understood",
    aiHint: "Same fields and conditions as the form. Values are a sample chat.",
    filledByAi: "Filled by the assistant",
    neverSent: "Never sent to the assistant",
    staffOnly: "Staff only: not asked",
    ask: "Ask the client",
    askRequired: "Ask the client (required)",
    staffTitle: "Fields panel on the ticket",
    staffHint: "Required to resolve blocks the Resolve dialog until the field is filled.",
    staffOnlyChip: "Staff only",
    requiredToResolve: "Required to resolve",
    samples: {
      asset: "Sample equipment · SN-0001",
      site: "Sample site · Main building",
      order: "Sample order · ORD-1001",
      contact: "Sample colleague",
    },
    sampleValues: {
      text: "Sample text",
      long_text: "A short description of what happened",
      number: "12.5",
      integer: "3",
      bool: "Yes",
      date: "Sample date",
      datetime: "Sample date and time",
      dropdown: "First option",
      multi_select: "First option",
      asset: "Sample equipment",
      site: "Sample site",
      order: "Sample order",
      contact: "Sample colleague",
      files: "1 photo",
      serial: "SN-0001",
    },
  },
} as const;
